import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Loader2, Eye, EyeOff, AlertCircle, ShieldCheck, Trophy, Zap } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';
import TenantHeader from '../components/TenantHeader';
import { motion } from 'framer-motion';
import { branding } from '../config/branding';
import { loginWithPasskey, PendingTwoFA } from '../services/passkeyAuth';
import TwoFactorModal from '../components/TwoFactorModal';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import AppSplash from '../components/AppSplash';
import { setPostAuthRedirect } from '../lib/postAuthRedirect';

const { primaryColor } = branding.theme;

interface SignInPageProps {
  /**
   * Where a successful sign-in lands. Defaults to the dashboard, which is
   * what the /sign-in route wants; an admin page rendering this in place of
   * itself passes its own path, so signing in shows that page rather than
   * bouncing through the dashboard.
   */
  redirectTo?: string;
  /**
   * True when this is the staff door (/manage) rather than a club's member
   * sign-in. A club may admit only its own email domain as members; that rule
   * must not shut out whoever runs the club, signing in on their own
   * ChessDirector account, so the staff door asks for a platform sign-in with
   * no club attached.
   */
  staffSignIn?: boolean;
}

const SignInPage: React.FC<SignInPageProps> = ({ redirectTo = '/dashboard', staffSignIn = false }) => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const { tenant, loading: tenantLoading } = useTenant();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingTwoFA, setPendingTwoFA] = useState<{ pendingToken: string; user: { id: string; username: string } } | null>(null);
  const passkeysAvailable = typeof window !== 'undefined' && 'PublicKeyCredential' in window;

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, redirectTo]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        login(data.data.token, data.data.user);
        setTimeout(async () => {
          try {
            const profileResponse = await fetch(`${API_BASE_URL}/auth/profile`, {
              headers: {
                'Authorization': `Bearer ${data.data.token}`,
              },
            });
            const profileData = await profileResponse.json();
            if (profileData.success && profileData.data?.user) {
              login(data.data.token, profileData.data.user);
            }
          } catch (err) {
            console.error('Failed to refresh profile after login:', err);
          }
        }, 100);
        navigate(redirectTo);
      } else {
        setError(data.error || 'Invalid username or password');
      }
    } catch (err: any) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
    // Google leaves the site and comes back through /auth/callback, which has
    // no idea where the sign-in started — so the landing page is left behind
    // for it to pick up.
    setPostAuthRedirect(redirectTo);
    // On a club's hostname the server reads the club from the host and applies
    // its member sign-in policy. `mode=admin` says this is the staff door, so
    // no club is attached and that policy does not apply.
    const mode = staffSignIn ? '?mode=admin' : '';
    window.location.href = `${API_BASE_URL}/auth/google${mode}`;
  };

  const handleLichessLogin = () => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
    setPostAuthRedirect(redirectTo);
    window.location.href = `${API_BASE_URL}/auth/lichess`;
  };

  const handlePasskeyLogin = async () => {
    if (!passkeysAvailable) {
      setError('Passkeys are not supported in this browser.');
      return;
    }

    setPasskeyLoading(true);
    setError(null);
    try {
      const result = await loginWithPasskey(formData.username);
      if ('require2FA' in result) {
        setPendingTwoFA({ pendingToken: result.pendingToken, user: result.user });
      } else {
        login(result.token, result.user);
        navigate(redirectTo);
      }
    } catch (err: any) {
      setError(err.message || 'Passkey sign-in failed. Please try again.');
    } finally {
      setPasskeyLoading(false);
    }
  };

  if (isLoading || tenantLoading) {
    return tenant || tenantLoading ? (
      <AppSplash className="bg-gray-50" />
    ) : (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
        <Loader2 className="w-8 h-8 animate-spin text-white/80" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans selection:bg-gray-200 selection:text-black antialiased">
      {tenant ? (
        <TenantHeader transparent={false} showSearch={false} />
      ) : (
        <PublicHeader />
      )}

      <main className="flex-1 pt-24 pb-16">
        <div className="container px-4 md:px-6 mx-auto max-w-6xl">
          {/* Neon console–style split: left branding, right form */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[calc(100vh-8rem)]">
            {/* Left: Connect to Pair Craft / branding */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
              className="hidden lg:block"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400 mb-6">
                Connect to {branding.companyName} with
              </p>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-4">
                {t('auth.logInToAccount', 'Log in to your account')}
              </h1>
              <p className="text-gray-500 text-lg mb-10 max-w-md">
                {tenant ? `Sign in to ${tenant.name} to manage tournaments and pairings.` : t('auth.welcomeBack', 'Enter your credentials to access your dashboard and run tournaments.')}
              </p>
              <ul className="space-y-4 text-sm text-gray-600">
                <li className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="w-4 h-4 text-gray-700" />
                  </span>
                  <span>USCF & FIDE compliant pairings</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-4 h-4 text-gray-700" />
                  </span>
                  <span>Unlimited tournaments on Organizer plan</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 text-gray-700" />
                  </span>
                  <span>Live broadcast and real-time updates</span>
                </li>
              </ul>
            </motion.div>

            {/* Right: Or log in with your username — form card */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto"
            >
              <div className="lg:hidden mb-8">
                <h1 className="text-2xl font-bold tracking-tight text-gray-900 mb-2">{t('auth.welcomeBack', 'Welcome back')}</h1>
                <p className="text-sm text-gray-500">{tenant ? `Sign in to ${tenant.name}` : t('auth.logInToAccount', 'Enter your credentials to access your account')}</p>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl shadow-xl shadow-gray-200/50 p-6 md:p-8">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-6">Or log in with your username</p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-50 border border-red-200 text-red-700 px-3 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2"
                    >
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{error}</span>
                    </motion.div>
                  )}

                  <div className="space-y-1.5">
                    <label htmlFor="username" className="block text-xs font-semibold text-gray-700">
                      {t('auth.username', 'Username')}
                    </label>
                    <input
                      id="username"
                      name="username"
                      type="text"
                      required
                      value={formData.username}
                      onChange={handleInputChange}
                      className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none transition-all text-gray-900 placeholder-gray-400 text-sm"
                      placeholder="Your username"
                      autoFocus
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label htmlFor="password" className="block text-xs font-semibold text-gray-700">
                      {t('auth.password', 'Password')}
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full px-3.5 py-2.5 pr-10 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none transition-all text-gray-900 placeholder-gray-400 text-sm"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full text-white px-4 py-3 rounded-lg font-bold disabled:opacity-70 transition-all shadow-sm flex items-center justify-center gap-2 text-sm mt-2"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('auth.signingIn', 'Signing in...')}
                      </>
                    ) : (
                      t('auth.signIn', 'Sign in')
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handlePasskeyLogin}
                    disabled={!passkeysAvailable || passkeyLoading}
                    className={`w-full px-4 py-3 rounded-lg font-bold transition-all text-sm ${!passkeysAvailable || passkeyLoading
                      ? 'bg-gray-100 border border-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-white border border-gray-300 text-gray-800 hover:bg-gray-50'
                      }`}
                  >
                    {passkeyLoading ? 'Signing in with passkey...' : `Sign in with Passkey${passkeysAvailable ? '' : ' (unsupported browser)'}`}
                  </button>
                </form>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-100" />
                  </div>
                  <div className="relative flex justify-center text-[10px] uppercase tracking-wider">
                    <span className="bg-white px-2 text-gray-400">{t('auth.orContinueWith', 'Or continue with')}</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all text-sm font-medium text-gray-700"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Google
                </button>

                <button
                  type="button"
                  onClick={handleLichessLogin}
                  className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all text-sm font-medium text-gray-700"
                >
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/da/Lichess_Logo_2019.svg/1280px-Lichess_Logo_2019.svg.png" alt="Lichess" className="w-4 h-4" />
                  Lichess
                </button>

                <p className="mt-8 text-center text-sm text-gray-500">
                  {t('auth.noAccount', "Don't have an account?")}{' '}
                  <Link
                    to="/sign-up"
                    className="font-semibold text-gray-900 hover:underline transition-colors"
                  >
                    {t('auth.signUp', 'Sign up')}
                  </Link>
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
      {pendingTwoFA && (
        <TwoFactorModal
          isOpen={!!pendingTwoFA}
          pendingToken={pendingTwoFA.pendingToken}
          user={pendingTwoFA.user}
          onSuccess={(token, user) => {
            setPendingTwoFA(null);
            login(token, user);
            navigate(redirectTo);
          }}
          onCancel={() => setPendingTwoFA(null)}
        />
      )}
    </div>
  );
};

export default SignInPage;
